// Cuadra (tienda v5) — port de maquetas/tienda-v5-vecinos.html a JSX.
// Página 12 de Fase 3 (LA ÚLTIMA). Ruta /cuadra (+ dev alias). Acento TEAL (Cuadra es un
// beneficio para clientes suscriptores, no un partnership como Porteros/Empresas).
// CSS co-locado bajo .tv5-page-cuadra.
//
// Nombre: la maqueta se llamaba tienda-v5-vecinos.html pero la ruta/producto final es
// "Cuadra" (decisión Fase 1). Componente, ruta y copy usan "Cuadra".
//
// Reusa shared: TopNav(teal) / Breadcrumb / Footer(teal) / BottomNav / MiniBar.
// Elementos (maqueta pulida Fase 1): hero + CTA anchor, proof (3), ejemplo de ahorro
// (Kit Familiar, 5% off), 3 pasos (incl. CTA "Compartir por WhatsApp"), 4 reglas de
// "qué cuenta como tu cuadra", FAQ propia (5), CTA final a Kits.
//
// El ejemplo de ahorro deriva del precio real del Kit Familiar en kits.js (1490) para no
// duplicar el número: 5% → $75/mes → $1.415/mes → $900/año (valores validados por Nico).
import { Link } from 'react-router-dom';
import { TopNav, Breadcrumb, Footer, BottomNav, MiniBar } from '../../components/tienda-v5';
import { TV5_ROUTES } from '../../components/tienda-v5/routes';
import { KITS } from '../../data/kits';

const DESCUENTO_CUADRA = 0.05; // 5% off permanente del Programa Cuadra

// Ejemplo de ahorro derivado del Kit Familiar (single source: kits.js).
const FAMILIAR = KITS.find((k) => k.codigo === 'familiar');
const EJEMPLO = (() => {
  const normal = FAMILIAR.precio; // 1490
  const ahorroMes = Math.round(normal * DESCUENTO_CUADRA); // 75
  return { normal, conDesc: normal - ahorroMes, ahorroMes, ahorroAnual: ahorroMes * 12 };
})();

// Formato $1.490 (miles con punto, sin dependencia de Intl/locale).
const fmt = (n) => '$' + String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

// CTA "Compartir por WhatsApp" — wa.me SIN número (abre el selector de contactos del user).
// Apunta a https://limpack.uy/cuadra (destino público que existirá en Fase 4).
const SHARE_TEXT =
  '¡Sumate a Limpack en nuestra cuadra! 🧼 Si llegamos a 10 vecinos suscriptos, todos tenemos 5% off permanente. Mirá cómo: https://limpack.uy/cuadra';
const WA_SHARE = `https://wa.me/?text=${encodeURIComponent(SHARE_TEXT)}`;

const PROOF = [
  { num: '5%', label: 'off permanente' },
  { num: '10', label: 'vecinos activos' },
  { num: '$0', label: 'costo para vos' },
];

const PASOS = [
  { n: '1', h: 'Suscribite a un kit', p: 'Elegís tu Kit Limpack (Solo, Familiar, Confort, Hogar+, Dúo, Exclusive) y arrancás. Vos ya sumás como 1 de los 10.' },
  { n: '2', h: 'Compartí con tus vecinos', p: 'Mandale a tu grupo de WhatsApp del edificio o la cuadra el link. Cuantos más se sumen, más rápido llegan a los 10.', share: true },
  { n: '3', h: 'Descuento automático', p: 'Cuando el sistema detecta 10 suscriptores activos en tu cuadra, todos ven el 5% off aplicado en el siguiente cobro. Sin trámite.' },
];

const REGLAS = [
  { n: '1', h: 'Misma calle, entre las 2 esquinas más cercanas', p: 'Ejemplo: si vivís en Rivera 3200, cuenta desde Rivera 3100 hasta Rivera 3299 (ambas veredas).' },
  { n: '2', h: 'Ambas veredas cuentan', p: 'Los vecinos de enfrente también suman al mismo grupo. Es una cuadra, no una vereda.' },
  { n: '3', h: 'Todos deben estar activos', p: 'Suscripción al día, sin pausa. Si un vecino pausa o cancela, se baja de los 10 y el descuento pausa hasta que vuelvan a completarse.' },
  { n: '4', h: 'Un edificio entero puede llegar solo', p: 'Si vivís en un edificio grande, con 10 aptos ya se activa aunque no salgas de la puerta. Programa Cuadra funciona joya con edificios.' },
];

const FAQ_CUADRA = [
  { q: '¿Cómo sé cuántos vecinos ya se suscribieron?', a: 'En tu cuenta ves un contador con los suscriptores activos de tu cuadra. Se actualiza en tiempo real. Cuando llegás a 10, te avisamos por WhatsApp que se activó el descuento.' },
  { q: '¿Qué pasa si se baja de 10 después de activado?', a: 'El descuento pausa desde el próximo cobro hasta que vuelva a haber 10 activos. Los meses ya cobrados con descuento no cambian.' },
  { q: '¿Puedo combinar con otras promos?', a: 'Sí. El 5% del Programa Cuadra se acumula con la bonificación de bienvenida (3 primeros meses). No se acumula con descuentos comerciales puntuales.' },
  { q: '¿Y si me mudo?', a: 'Cambiás la dirección desde tu cuenta y automáticamente pasás al conteo de tu nueva cuadra. Si la anterior tenía el descuento y bajaste a 9, los que quedan se enteran por WhatsApp.' },
  { q: '¿Comparte información privada con mis vecinos?', a: 'No. Nunca ves nombres, direcciones exactas ni consumos de otros. Solo el contador total de suscriptores activos en tu cuadra (Ley 18.331 de protección de datos).' },
];

export default function Cuadra() {
  return (
    <div className="tv5-page-cuadra">
      <style>{CUADRA_CSS}</style>

      <TopNav active="cuadra" accent="teal" />
      <Breadcrumb backHref={TV5_ROUTES.home} backLabel="Home" current="Programa Cuadra" />

      <main>
        {/* HERO */}
        <section className="cu-hero">
          <span className="cu-eyebrow">Programa Cuadra</span>
          <h1>10 vecinos en tu cuadra = <em>5% off</em> permanente.</h1>
          <p>Si 10 personas de tu cuadra están suscriptas a Limpack, todos ganan 5% de descuento automático mientras se mantengan los 10. Sin trámite, sin comprar más, sin nada. Solo compartir.</p>
          <a href="#como-funciona" className="cu-hero-cta">Ver cómo funciona ↓</a>
        </section>

        {/* PROOF */}
        <div className="cu-proof">
          {PROOF.map((p) => (
            <div className="cu-proof-item" key={p.label}>
              <div className="cu-proof-num">{p.num}</div>
              <div className="cu-proof-label">{p.label}</div>
            </div>
          ))}
        </div>

        {/* CUÁNTO AHORRÁS */}
        <section className="cu-seccion">
          <h2>Cuánto ahorrás</h2>
          <p className="cu-seccion-sub">El 5% aplica sobre el precio mensual de tu kit. Se acumula todos los meses.</p>

          <div className="cu-ejemplo">
            <div className="cu-ejemplo-titulo">Ejemplo con Kit Familiar</div>
            <h3>10 vecinos + Kit Familiar {fmt(EJEMPLO.normal)}/mes</h3>
            <div className="cu-ejemplo-lineas">
              <div className="cu-ejemplo-linea">
                <span>Precio normal</span>
                <span className="cu-ejemplo-linea-val">{fmt(EJEMPLO.normal)}/mes</span>
              </div>
              <div className="cu-ejemplo-linea">
                <span>Con 5% off del Programa Cuadra</span>
                <span className="cu-ejemplo-linea-val">{fmt(EJEMPLO.conDesc)}/mes</span>
              </div>
              <div className="cu-ejemplo-linea">
                <span>Ahorro por mes</span>
                <span className="cu-ejemplo-linea-val">{fmt(EJEMPLO.ahorroMes)}</span>
              </div>
            </div>
            <div className="cu-ejemplo-total">
              <span>Ahorro anual</span>
              <span className="cu-ejemplo-total-num">{fmt(EJEMPLO.ahorroAnual)}</span>
            </div>
          </div>
        </section>

        {/* CÓMO FUNCIONA */}
        <section className="cu-seccion" id="como-funciona">
          <h2>Cómo funciona</h2>
          <p className="cu-seccion-sub">3 pasos y el descuento se activa solo. No hay que pedir nada.</p>

          <div className="cu-pasos">
            {PASOS.map((p) => (
              <div className="cu-paso" key={p.n}>
                <span className="cu-paso-num">{p.n}</span>
                <h3>{p.h}</h3>
                <p>{p.p}</p>
                {p.share && (
                  <a href={WA_SHARE} target="_blank" rel="noopener noreferrer" className="cu-paso-share">
                    💬 Compartir por WhatsApp
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* QUÉ CUENTA COMO CUADRA */}
        <section className="cu-seccion">
          <h2>¿Qué cuenta como "tu cuadra"?</h2>
          <p className="cu-seccion-sub">Definición simple y transparente para que nadie se pierda.</p>

          <div className="cu-reglas">
            {REGLAS.map((r) => (
              <div className="cu-regla" key={r.n}>
                <span className="cu-regla-bullet">{r.n}</span>
                <div className="cu-regla-content">
                  <h4>{r.h}</h4>
                  <p>{r.p}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="cu-seccion">
          <h2>Dudas rápidas</h2>
          <div className="cu-faq">
            {FAQ_CUADRA.map((f) => (
              <details className="cu-faq-item" key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA FINAL */}
        <div className="cu-cta-fin">
          <h3>Sumate y arrancá a juntar tu cuadra</h3>
          <p>Suscribite hoy y compartí el link con tus vecinos. Cuando lleguen a 10, el 5% off es automático.</p>
          <Link to={TV5_ROUTES.kits} className="cu-cta-fin-btn">Ver kits →</Link>
        </div>
      </main>

      <MiniBar checkoutHref={TV5_ROUTES.checkout} />
      <Footer accent="teal" />
      <BottomNav active="cuadra" />
    </div>
  );
}

const CUADRA_CSS = `
.tv5-page-cuadra {
  --tv5cu-navy:#1A3461; --tv5cu-teal:#00A8B5; --tv5cu-gold:#C5A059; --tv5cu-green:#10B981;
  --tv5cu-bg:#0A1424; --tv5cu-card:rgba(255,255,255,0.04); --tv5cu-border:rgba(255,255,255,0.08);
  --tv5cu-text:#F8F6F1; --tv5cu-muted:rgba(248,246,241,0.65); --tv5cu-dim:rgba(248,246,241,0.45);
  --safe-bottom:env(safe-area-inset-bottom, 0px);
  background:var(--tv5cu-bg); color:var(--tv5cu-text); min-height:100vh; position:relative;
  overflow-x:clip; max-width:100%;
  font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
  padding-bottom:calc(72px + var(--safe-bottom));
}
.tv5-page-cuadra::before {
  content:''; position:fixed; inset:0; pointer-events:none; z-index:0;
  background:
    radial-gradient(circle at 20% 10%, rgba(0,168,181,0.10) 0%, transparent 40%),
    radial-gradient(circle at 80% 60%, rgba(16,185,129,0.08) 0%, transparent 40%);
}
.tv5-page-cuadra main { position:relative; z-index:1; }
@media (min-width:900px){ .tv5-page-cuadra { padding-bottom:0; } }

/* HERO */
.tv5-page-cuadra .cu-hero { padding:40px 20px 20px; text-align:center; max-width:900px; margin:0 auto; }
.tv5-page-cuadra .cu-eyebrow { display:inline-block; color:var(--tv5cu-teal); font-size:11px; font-weight:800; letter-spacing:0.15em; text-transform:uppercase; padding:6px 14px; background:rgba(0,168,181,0.1); border:1px solid rgba(0,168,181,0.3); border-radius:999px; margin-bottom:14px; }
.tv5-page-cuadra .cu-hero h1 { font-size:clamp(30px, 6.5vw, 52px); font-weight:800; letter-spacing:-0.03em; line-height:1.05; margin-bottom:14px; background:linear-gradient(135deg, var(--tv5cu-text) 0%, var(--tv5cu-teal) 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
.tv5-page-cuadra .cu-hero h1 em { font-style:normal; color:var(--tv5cu-teal); -webkit-text-fill-color:var(--tv5cu-teal); }
.tv5-page-cuadra .cu-hero p { color:var(--tv5cu-muted); font-size:15px; max-width:560px; margin:0 auto 20px; line-height:1.6; }
.tv5-page-cuadra .cu-hero-cta { display:inline-flex; align-items:center; gap:6px; background:linear-gradient(135deg, var(--tv5cu-teal) 0%, #00C4D4 100%); color:var(--tv5cu-navy); padding:14px 28px; border-radius:10px; font-weight:800; font-size:14px; text-decoration:none; box-shadow:0 8px 24px rgba(0,168,181,0.35); min-height:44px; transition:transform 0.2s; }
@media (hover:hover){ .tv5-page-cuadra .cu-hero-cta:hover { transform:translateY(-2px); } }

/* PROOF */
.tv5-page-cuadra .cu-proof { max-width:900px; margin:0 auto; padding:20px; display:grid; grid-template-columns:repeat(3, 1fr); gap:12px; text-align:center; }
.tv5-page-cuadra .cu-proof-item { background:var(--tv5cu-card); border:1px solid var(--tv5cu-border); border-radius:14px; padding:18px 12px; }
.tv5-page-cuadra .cu-proof-num { font-size:clamp(20px, 4vw, 28px); font-weight:900; color:var(--tv5cu-teal); letter-spacing:-0.02em; margin-bottom:4px; }
.tv5-page-cuadra .cu-proof-label { font-size:11px; color:var(--tv5cu-muted); font-weight:600; letter-spacing:0.03em; }

/* SECCIÓN */
.tv5-page-cuadra .cu-seccion { padding:40px 20px; max-width:1000px; margin:0 auto; }
.tv5-page-cuadra .cu-seccion h2 { font-size:clamp(22px, 4vw, 32px); font-weight:800; letter-spacing:-0.02em; margin-bottom:8px; text-align:center; }
.tv5-page-cuadra .cu-seccion-sub { text-align:center; color:var(--tv5cu-muted); font-size:14px; margin:0 auto 32px; max-width:500px; line-height:1.5; }

/* EJEMPLO AHORRO */
.tv5-page-cuadra .cu-ejemplo { background:linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(0,168,181,0.08) 100%); border:1px solid rgba(16,185,129,0.25); border-radius:20px; padding:28px 24px; max-width:640px; margin:0 auto; }
.tv5-page-cuadra .cu-ejemplo-titulo { font-size:12px; color:var(--tv5cu-green); font-weight:800; letter-spacing:0.1em; text-transform:uppercase; margin-bottom:6px; text-align:center; }
.tv5-page-cuadra .cu-ejemplo h3 { font-size:20px; font-weight:800; margin-bottom:18px; text-align:center; letter-spacing:-0.02em; }
.tv5-page-cuadra .cu-ejemplo-lineas { display:flex; flex-direction:column; gap:12px; margin-bottom:20px; }
.tv5-page-cuadra .cu-ejemplo-linea { display:flex; justify-content:space-between; align-items:center; gap:12px; font-size:14px; color:var(--tv5cu-muted); padding-bottom:10px; border-bottom:1px dashed var(--tv5cu-border); }
.tv5-page-cuadra .cu-ejemplo-linea:last-child { border-bottom:0; padding-bottom:0; }
.tv5-page-cuadra .cu-ejemplo-linea-val { color:var(--tv5cu-text); font-weight:700; white-space:nowrap; }
.tv5-page-cuadra .cu-ejemplo-total { display:flex; justify-content:space-between; align-items:center; gap:12px; padding-top:16px; border-top:2px solid rgba(16,185,129,0.4); font-size:16px; font-weight:800; }
.tv5-page-cuadra .cu-ejemplo-total-num { color:var(--tv5cu-green); font-size:22px; letter-spacing:-0.02em; white-space:nowrap; }

/* PASOS */
.tv5-page-cuadra .cu-pasos { display:grid; gap:14px; margin-top:8px; }
@media (min-width:640px){ .tv5-page-cuadra .cu-pasos { grid-template-columns:repeat(3, 1fr); } }
.tv5-page-cuadra .cu-paso { background:var(--tv5cu-card); border:1px solid var(--tv5cu-border); border-radius:16px; padding:22px 20px; position:relative; }
.tv5-page-cuadra .cu-paso-num { display:inline-flex; align-items:center; justify-content:center; width:36px; height:36px; border-radius:50%; background:rgba(0,168,181,0.15); border:1px solid rgba(0,168,181,0.4); color:var(--tv5cu-teal); font-size:14px; font-weight:900; margin-bottom:12px; }
.tv5-page-cuadra .cu-paso h3 { font-size:16px; font-weight:800; margin-bottom:6px; letter-spacing:-0.01em; }
.tv5-page-cuadra .cu-paso p { color:var(--tv5cu-muted); font-size:13px; line-height:1.5; }
.tv5-page-cuadra .cu-paso-share { display:inline-flex; align-items:center; justify-content:center; gap:6px; margin-top:14px; width:100%; background:#25D366; color:#06371C; padding:12px 16px; border-radius:10px; min-height:44px; font-weight:800; font-size:13px; text-decoration:none; box-shadow:0 6px 18px rgba(37,211,102,0.3); transition:transform 0.2s; }
@media (hover:hover){ .tv5-page-cuadra .cu-paso-share:hover { transform:translateY(-2px); } }

/* QUÉ CUENTA COMO CUADRA */
.tv5-page-cuadra .cu-reglas { background:var(--tv5cu-card); border:1px solid var(--tv5cu-border); border-radius:16px; padding:24px 22px; max-width:640px; margin:0 auto; display:grid; gap:14px; }
.tv5-page-cuadra .cu-regla { display:flex; gap:12px; align-items:flex-start; }
.tv5-page-cuadra .cu-regla-bullet { flex-shrink:0; width:28px; height:28px; border-radius:50%; background:rgba(0,168,181,0.15); border:1px solid rgba(0,168,181,0.4); color:var(--tv5cu-teal); font-size:13px; font-weight:900; display:inline-flex; align-items:center; justify-content:center; }
.tv5-page-cuadra .cu-regla-content h4 { font-size:14px; font-weight:800; margin-bottom:4px; }
.tv5-page-cuadra .cu-regla-content p { color:var(--tv5cu-muted); font-size:13px; line-height:1.5; }

/* FAQ */
.tv5-page-cuadra .cu-faq { display:grid; gap:10px; max-width:700px; margin:0 auto; }
.tv5-page-cuadra .cu-faq-item { background:var(--tv5cu-card); border:1px solid var(--tv5cu-border); border-radius:12px; overflow:hidden; transition:border-color 0.2s; }
.tv5-page-cuadra .cu-faq-item[open] { border-color:rgba(0,168,181,0.35); }
.tv5-page-cuadra .cu-faq-item summary { padding:16px 18px; cursor:pointer; font-size:14px; font-weight:700; display:flex; justify-content:space-between; align-items:center; gap:12px; list-style:none; }
.tv5-page-cuadra .cu-faq-item summary::-webkit-details-marker { display:none; }
.tv5-page-cuadra .cu-faq-item summary::after { content:'+'; font-size:22px; color:var(--tv5cu-teal); font-weight:300; flex-shrink:0; transition:transform 0.2s; }
.tv5-page-cuadra .cu-faq-item[open] summary::after { transform:rotate(45deg); }
.tv5-page-cuadra .cu-faq-item p { padding:0 18px 18px; color:var(--tv5cu-muted); font-size:13px; line-height:1.6; }

/* CTA FINAL */
.tv5-page-cuadra .cu-cta-fin { max-width:700px; margin:60px auto 0; padding:40px 24px; background:linear-gradient(135deg, var(--tv5cu-teal) 0%, #00C4D4 100%); border-radius:20px; text-align:center; color:var(--tv5cu-navy); box-shadow:0 20px 60px rgba(0,168,181,0.35); }
.tv5-page-cuadra .cu-cta-fin h3 { font-size:26px; font-weight:900; margin-bottom:8px; letter-spacing:-0.02em; }
.tv5-page-cuadra .cu-cta-fin p { font-size:14px; margin-bottom:24px; opacity:0.85; }
.tv5-page-cuadra .cu-cta-fin-btn { display:inline-flex; align-items:center; gap:6px; background:var(--tv5cu-navy); color:#fff; padding:16px 32px; border-radius:10px; font-weight:800; font-size:15px; text-decoration:none; min-height:44px; transition:transform 0.2s; }
@media (hover:hover){ .tv5-page-cuadra .cu-cta-fin-btn:hover { transform:translateY(-2px); } }

/* Reduced motion */
@media (prefers-reduced-motion:reduce){
  .tv5-page-cuadra .cu-hero-cta, .tv5-page-cuadra .cu-paso-share, .tv5-page-cuadra .cu-faq-item,
  .tv5-page-cuadra .cu-faq-item summary::after, .tv5-page-cuadra .cu-cta-fin-btn { transition:none; }
  .tv5-page-cuadra { scroll-behavior:auto; }
}
`;
